import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { Inject, ModuleWithProviders, NgModule, Type } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isDefined, NotificationInterface, PaceCoreModule, TranslationService } from '@evorait/pace-core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { LetDirective, PushPipe } from '@ngrx/component';
import { EffectsModule } from '@ngrx/effects';
import { ActionReducerMap, StoreModule } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { tap } from 'rxjs/operators';
import { BaseFormsListComponent } from './components/base-forms-list/base-forms-list.component';
import { CheckpointComponent } from './components/checkpoint/checkpoint.component';
import { ChoicesComponent } from './components/choices/choices.component';
import { ContentRendererComponent } from './components/content-renderer/content-renderer.component';
import { StatusIconPipe } from './components/content-renderer/status-icon.pipe';
import { CustomerIdentificationInputFieldComponent } from './components/customer-identification-input-field/customer-identification-input-field.component';
import { CustomerIdentificationComponent } from './components/customer-identification/customer-identification.component';
import { DatePerformedComponent } from './components/date-performed/date-performed.component';
import { DefectServicePartIdentificationComponent } from './components/defect-service-part-identification/defect-service-part-identification.component';
import { DefectiveComponentReplacementComponent } from './components/defective-component-replacement/defective-component-replacement.component';
import { FormsDurationManualInputComponent } from './components/defective-component-replacement/forms-duration-manual-input/forms-duration-manual-input.component';
import { DiscrepancyLogComponent } from './components/discrepancy-log/discrepancy-log.component';
import { DocBoxListIconPipe } from './components/docbox/docbox-list-icon.pipe';
import { DocboxComponent } from './components/docbox/docbox.component';
import { DocumentListEntryComponent } from './components/docbox/document-list-entry/document-list-entry.component';
import { FileUploadComponent } from './components/docbox/upload-modal/file-upload/file-upload.component';
import { UploadModalComponent } from './components/docbox/upload-modal/upload-modal.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { FilterComponent } from './components/filter/filter.component';
import { FormsAccordionComponent } from './components/forms-accordion/forms-accordion.component';
import { FormsListEntryCssClassPipe } from './components/forms-list/forms-list-pipes/forms-list-entry-css-class.pipe';
import { FormsListIconPipe } from './components/forms-list/forms-list-pipes/forms-list-icon.pipe';
import { FormsListComponent, FormsListComponentInstanceToken } from './components/forms-list/forms-list.component';
import { FormsListEntryComponent } from './components/forms-list/forms-list-entry/forms-list-entry.component';
import { Forms2NavbarComponent } from './components/forms2-navbar/forms2-navbar.component';
import { Forms2RendererComponent } from './components/forms2-renderer/forms2-renderer.component';
import { Forms2Component } from './components/forms2/forms2.component';
import { FunctionalElementComponent } from './components/functional-element/functional-element.component';
import { HintsForFillingComponent } from './components/hints-for-filling/hints-for-filling.component';
import { InputRadioComponent } from './components/input-radio/input-radio.component';
import { InputTextComponent } from './components/input-text/input-text.component';
import { MaterialSerialNumberComponent } from './components/material-serial-number/material-serial-number.component';
import { AstoriaOptionComponent } from './components/option/astoria-option/astoria-option.component';
import { BaseOptionComponent } from './components/option/base-option.component';
import { CosimaOptionComponent } from './components/option/cosima-option/cosima-option.component';
import { AddParticipantComponent } from './components/participant-copies/add-participant/add-participant.component';
import { CopyChecklistSignatureComponent } from './components/participant-copies/copy-checklist-signature/copy-checklist-signature.component';
import { ParticipantCopiesComponent } from './components/participant-copies/participant-copies.component';
import { ParticipantListEntryComponent } from './components/participant-copies/participant-list-entry/participant-list-entry.component';
import { RecipientListEntryComponent } from './components/recipients/recipient-list-entry/recipient-list-entry.component';
import { RecipientsComponent } from './components/recipients/recipients.component';
import { ReplacementOrLastProcedureDateComponent } from './components/replacement-or-last-procedure-date/replacement-or-last-procedure-date.component';
import { SelectParticipantCopyComponent } from './components/select-participant-copy/select-participant-copy.component';
import { SignatureComponent } from './components/signature/signature.component';
import { StandardFunctionalElementComponent } from './components/standard-functional-element/standard-functional-element.component';
import { SystemConditionSignatureComponent } from './components/system-condition/system-condition-signature.component';
import { SystemConditionComponent } from './components/system-condition/system-condition.component';
import { ValueComponent } from './components/value/value.component';
import { XmlRendererComponent } from './components/xml-renderer/xml-renderer.component';
import { NotificationInstanceToken } from './core/interfaces/notification.interface';
import { TranslationInstanceToken, TranslationInterface } from './core/interfaces/translation.interface';
import { FormsInterceptor } from './services/forms-interceptor';
import { FormsWriteInstanceToken, FormsWriteInterface } from './services/forms-write.interface';
import { DocBoxEffectsService } from './store/effects/docbox-effects.service';
import { FormDeleteEffects } from './store/effects/form-delete-effects.service';
import { FormEditEffects } from './store/effects/form-edit-effects.service';
import { FormEffects } from './store/effects/form-effects.service';
import { FormLoadEffects } from './store/effects/form-load-effects.service';
import { FormParticipantsEffects } from './store/effects/form-participants-effects.service';
import { FormSaveEffects } from './store/effects/form-save-effects.service';
import { ServicePartQuestionnaireEffects } from './store/effects/service-part-questionnaire-effects.service';
import { docBoxReducer } from './store/reducers/docBox.reducer';
import { formListsReducer } from './store/reducers/form-lists.reducer';
import { formReducer } from './store/reducers/form.reducer/index';
import { lastOpenedFormReducer } from './store/reducers/last-opened-form.reducer';
import { PaceFormsState } from './store/state/pace-forms.state';
import { DropDownOptionsFunctionalElementComponent } from './components/drop-down-options-functional-element/drop-down-options-functional-element.component';
import { IndividualizationMaterialSerialComponent } from './components/individualization-material-serial/individualization-material-serial.component';
import { DropdownOptionsComponent } from './components/dropdown-options/dropdown-options.component';
import { MeasurementEquipmentsComponent } from './components/measurement-equpiments/measurement-equipments.component';
import { MeasuringEquipmentsInputRowComponent } from './components/measuring-equpiments-input-row/measuring-equipments-input-row.component';
import { DocBoxFilterHeaderComponent } from './components/docbox/docbox-filter-header/docbox-filter-header.component';
import { DocBoxDocumentRowComponent } from './components/docbox/docbox-document-row/docbox-document-row.component';
import { DocumentFeedbackComponent } from './components/document-feedback/document-feedback.component';
import { serviceActivityReducer } from './store/reducers/service-activity.reducer';
import { FormsListSubmitStatusPipe } from './components/forms-list/forms-list-pipes/forms-list-submit-status.pipe';
import { HistoryButtonComponent } from './components/historical-values/history-button/history-button.component';
import { HistoricalValuesModalComponent } from './components/historical-values/historical-values-modal/historical-values-modal.component';
import { HistoricalValuesEffectsService } from './store/effects/historical-values-effects.service';

export const reducers: ActionReducerMap<PaceFormsState> = {
    form: formReducer,
    lists: formListsReducer,
    lastOpenedForm: lastOpenedFormReducer,
    docBox: docBoxReducer,
    serviceActivity: serviceActivityReducer,
};

@NgModule({
    declarations: [
        Forms2Component,
        CustomerIdentificationInputFieldComponent,
        CustomerIdentificationComponent,
        DefectServicePartIdentificationComponent,
        XmlRendererComponent,
        ContentRendererComponent,
        CheckpointComponent,
        DiscrepancyLogComponent,
        SignatureComponent,
        FunctionalElementComponent,
        MaterialSerialNumberComponent,
        FormsListComponent,
        FormsListEntryComponent,
        DatePerformedComponent,
        ReplacementOrLastProcedureDateComponent,
        ParticipantCopiesComponent,
        AddParticipantComponent,
        CopyChecklistSignatureComponent,
        ParticipantListEntryComponent,
        FilterComponent,
        SelectParticipantCopyComponent,
        Forms2NavbarComponent,
        Forms2RendererComponent,
        FormsAccordionComponent,
        ValueComponent,
        SystemConditionComponent,
        SystemConditionSignatureComponent,
        HintsForFillingComponent,
        RecipientsComponent,
        RecipientListEntryComponent,
        FormsListIconPipe,
        FormsListSubmitStatusPipe,
        FormsListEntryCssClassPipe,
        StandardFunctionalElementComponent,
        DefectiveComponentReplacementComponent,
        InputTextComponent,
        DropdownComponent,
        DropdownOptionsComponent,
        StatusIconPipe,
        BaseOptionComponent,
        AstoriaOptionComponent,
        CosimaOptionComponent,
        InputRadioComponent,
        ChoicesComponent,
        FormsDurationManualInputComponent,
        DocboxComponent,
        DocBoxFilterHeaderComponent,
        DocBoxDocumentRowComponent,
        DocBoxListIconPipe,
        DocumentListEntryComponent,
        UploadModalComponent,
        FileUploadComponent,
        DropDownOptionsFunctionalElementComponent,
        IndividualizationMaterialSerialComponent,
        MeasurementEquipmentsComponent,
        MeasuringEquipmentsInputRowComponent,
        DocumentFeedbackComponent,
        HistoryButtonComponent,
        HistoricalValuesModalComponent,
    ],
    imports: [
        CommonModule,
        StoreModule.forFeature('paceForms', reducers),
        EffectsModule.forFeature([
            FormEffects,
            FormParticipantsEffects,
            FormLoadEffects,
            FormEditEffects,
            FormSaveEffects,
            FormDeleteEffects,
            DocBoxEffectsService,
            ServicePartQuestionnaireEffects,
            HistoricalValuesEffectsService,
        ]),
        ProgressbarModule,
        BsDropdownModule.forRoot(),
        FontAwesomeModule,
        TooltipModule,
        TypeaheadModule.forRoot(),
        FormsModule,
        TranslateModule.forChild(),
        ReactiveFormsModule,
        PaceCoreModule.forChild(),
        LetDirective,
        PushPipe,
    ],
    exports: [Forms2Component, FormsListComponent, DropdownComponent, DocboxComponent],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FormsInterceptor,
            multi: true,
        },
    ],
})
export class PaceForms2Module {
    public constructor(
        private readonly translationService: TranslationService,
        private readonly translate: TranslateService,
        @Inject(TranslationInstanceToken) private readonly translationMessages: TranslationInterface,
    ) {
        // load default language translation (for fallback)
        const defaultTranslation = <{ [idx: string]: string }>(
            // eslint-disable-next-line @typescript-eslint/no-var-requires,@typescript-eslint/no-require-imports
            require(`libs/pace-forms/src/lib/assets/i18n/${this.translationService.defaultLanguageKey}.json`)
        );
        this.translate.setTranslation(this.translationService.defaultLanguageKey, defaultTranslation, true);
        this.setCustomTranslations(this.translationService.defaultLanguageKey);

        this.translationService.currentLanguage$
            .pipe(
                tap((lang) => {
                    if (lang !== this.translationService.defaultLanguageKey) {
                        // load device's language translation
                        const translation = <{ [index: string]: string }>(
                            // eslint-disable-next-line @typescript-eslint/no-var-requires,@typescript-eslint/no-require-imports
                            require(`libs/pace-forms/src/lib/assets/i18n/${lang}.json`)
                        );
                        this.translate.setTranslation(lang, translation, true);
                        this.setCustomTranslations(lang);
                    }

                    this.translate.use(lang);
                }),
            )
            .subscribe();
    }

    /**
     * Should only be used from the main app-module, to prevent several service instances
     *
     * @param configuration configuration object
     * @returns the module with providers
     */
    public static forRoot(configuration: PaceFormsModuleConfiguration): ModuleWithProviders<PaceForms2Module> {
        return {
            ngModule: PaceForms2Module,
            providers: [
                { provide: NotificationInstanceToken, useExisting: configuration.notificationService },
                { provide: FormsWriteInstanceToken, useExisting: configuration.formsWriteService },
                {
                    provide: FormsListComponentInstanceToken,
                    useValue: configuration.formListComponent ?? FormsListComponent,
                },
                {
                    provide: TranslationInstanceToken,
                    useValue: configuration.translationMessages ?? {},
                },
            ],
        };
    }

    /**
     * Can be used from everywhere, after the module has been imported into the main app-module.
     *
     * @returns the module with providers
     */
    public static forChild(): ModuleWithProviders<PaceForms2Module> {
        return {
            ngModule: PaceForms2Module,
        };
    }

    private setCustomTranslations(language: string): void {
        // only supporting en and de as of now.
        const languageToUse = language === 'de' ? language : 'en';

        const selectedTranslationMessages = this.translationMessages[languageToUse];
        // set the passed in keys to overwrite the default keys in pace forms
        if (isDefined(selectedTranslationMessages) && Object.keys(selectedTranslationMessages).length > 0) {
            Object.keys(selectedTranslationMessages).forEach((key) =>
                this.translate.set(key, selectedTranslationMessages[key]),
            );
        }
    }
}

// extend store interface to include paceForms property
export interface PaceState {
    paceForms: PaceFormsState;
}

export interface PaceFormsModuleConfiguration {
    notificationService: Type<NotificationInterface>;
    formsWriteService: Type<FormsWriteInterface>;
    formListComponent?: Type<BaseFormsListComponent>;
    translationMessages?: TranslationInterface;
}
